import { useState, useCallback } from 'react'
import { Button } from 'react-bootstrap'

import { formatPhoneNumber } from '../../../../utils/utils'
import { useSession } from '../../../../contexts/SessionContext'
import OTPChannelToggle from '../../../elements/v2/OTPChannelToggle'
import ErrorToast from '../../../elements/v2/ErrorToast'
import {
  validateEmail,
  validatePhoneNumber,
} from '../../../../validators/DetailsValidator'

const EmailVerificationScreen = ({
  userEmail,
  setUserEmail,
  userPhone,
  setUserPhone,
  showOTPVerification,
  otpError,
  setOtpError,
  otpChannel,
  setOtpChannel,
  setOtpSessionId,
  sessionKey,
  sessionReference,
  setSessionKey,
  setSessionReference,
}) => {
  const { updateSessionData, startOtpVerification, fundingSourceStatus } =
    useSession()

  const [loading, setLoading] = useState(false)
  const isPhoneActive = otpChannel === 'sms'
  const isEmailValid = validateEmail(userEmail)
  const isPhoneValid = validatePhoneNumber(userPhone)

  const toggleOtpChannel = () => {
    setOtpChannel(isPhoneActive ? 'email' : 'sms')
  }

  const errorInputStyle = {
    boxShadow: '0 0 0 4px rgba(253,162,155,0.5)',
    borderColor: '#F04438',
    color: '#F04438',
  }

  const handleProceed = useCallback(async () => {
    setLoading(true)
    setOtpError('')

    try {
      const updateResponse = await updateSessionData(sessionKey, {
        email: userEmail,
        phoneNumber: userPhone,
      })

      if (updateResponse.statusCode === '0') {
        setSessionReference(updateResponse.data.customer_reference)

        // Start OTP verification
        const otpResponse = await startOtpVerification(sessionKey, {
          verificationType: otpChannel.toUpperCase(),
          email: userEmail,
          phoneNumber: userPhone,
          channel: otpChannel,
          reference: sessionReference,
        })

        if (otpResponse.statusCode === '0') {
          const otpSession = otpResponse.data.otpSessions?.[0]
          if (otpSession) {
            setOtpSessionId(otpSession.otpSessionId)
          }
          showOTPVerification()
        } else {
          setOtpError(otpResponse.statusMessage || 'Failed to start OTP verification')
        }
      } else {
        setOtpError(updateResponse.statusMessage || 'Failed to update session')
      }
    } catch (error) {
      console.error('Error starting direct debit session:', error)
      setOtpError(
        error.statusMessage || 'An error occurred while starting the session'
      )
    } finally {
      setLoading(false)
    }
  }, [userEmail, userPhone, otpChannel, showOTPVerification, setOtpError])

  return (
    <div
      style={{
        height: '100%',
        padding: 'var(--dd-content-padding-y) var(--dd-content-padding-x)',
      }}
      className='w-100 d-flex flex-column justify-content-between'
    >
      <div>
      <form className='pt-3'>
        <div>
          <label
            htmlFor='userEmailAddress'
            className='d-block text-start fw-medium mb-2'
            style={{ fontSize: 'var(--dd-label-size)' }}
          >
            Email address<span style={{ color: '#cf0000' }}>*</span>
          </label>
          <input
            type='email'
            style={{
              height: '42px',
              fontSize: 'var(--dd-input-size)',
              ...(otpError && !isEmailValid ? errorInputStyle : {}),
            }}
            className='form-control'
            id='userEmailAddress'
            aria-describedby='emailHelp'
            value={userEmail}
            onChange={(e) => {
              setUserEmail(e.target.value)
              if (otpError) setOtpError('')
            }}
            disabled={loading}
          />
        </div>

        <div className='my-3'>
          <label
            htmlFor='userPhoneNumber'
            className='d-block text-start fw-medium mb-2'
            style={{ fontSize: 'var(--dd-label-size)' }}
          >
            Phone number<span style={{ color: '#cf0000' }}>*</span>
          </label>
          <input
            type='text'
            style={{
              height: '42px',
              fontSize: 'var(--dd-input-size)',
              ...(otpError && !isPhoneValid ? errorInputStyle : {}),
            }}
            className='form-control'
            id='userPhoneNumber'
            value={userPhone}
            onChange={(e) => {
              const numericValue = e.target.value
                .replace(/[^\d+]/g, '')
                .replace(/\+/g, (match, offset) => (offset === 0 ? match : ''))
              setUserPhone(numericValue)
              if (otpError) setOtpError('')
            }}
            disabled={loading}
          />
        </div>
      </form>

      <div
        className='rounded-3'
        style={{
          border: '1px solid #EAECF0',
          boxShadow: '0px 1px 0.5px 0px #00000008',
        }}
      >
        <OTPChannelToggle
          otpChannel={otpChannel}
          formattedUserPhone={formatPhoneNumber(userPhone)}
          onToggle={toggleOtpChannel}
        />
      </div>

      <ErrorToast message={otpError} className='mt-3' />
      </div>

      <Button
        style={{
          background: isEmailValid && isPhoneValid ? '#F26522' : '#FCE9D2',
          padding: 'var(--dd-button-padding)',
          fontSize: 'var(--dd-button-text-size)',
          marginTop: '24px',
        }}
        className='w-100 rounded-pill text-white border-0'
        onClick={handleProceed}
        disabled={loading || !isEmailValid || !isPhoneValid}
      >
        {loading ? 'Processing...' : 'Proceed'}
      </Button>
    </div>
  )
}

export default EmailVerificationScreen
